import type { ConnectionMetrics } from '../stats';

export interface DiagnosticsPanelProps {
  readonly metrics: ConnectionMetrics | null;
  readonly iceState: RTCIceConnectionState | null;
}

export function DiagnosticsPanel({ metrics, iceState }: DiagnosticsPanelProps) {
  const rows = [
    ['Path', formatPath(metrics)],
    ['Codec', formatCodec(metrics)],
    ['Resolution', formatResolution(metrics)],
    ['FPS', formatNumber(metrics?.fps, 1)],
    ['Bitrate', formatBitrate(metrics?.outboundBitrateBps)],
    ['RTT', formatMilliseconds(metrics?.rttMs)],
    ['Packet loss', formatPercentage(metrics?.packetLossPercentage)],
    ['Jitter', formatMilliseconds(metrics?.jitterMs)],
    ['ICE state', formatIceState(iceState)],
  ] as const;

  return (
    <details class="diagnostics">
      <summary>
        <span>Advanced diagnostics</span>
        <ChevronIcon />
      </summary>
      <dl>
        {rows.map(([label, value]) => (
          <div class="diagnostics__row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export function formatBitrate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)} Mbps`;
  return `${Math.round(value / 1_000)} kbps`;
}

function formatPath(metrics: ConnectionMetrics | null): string {
  if (metrics === null || metrics.connectionPath === 'unknown') return 'Connecting';
  const path = metrics.connectionPath === 'relay' ? 'TURN relay' : 'Direct';
  const types = [metrics.localCandidateType, metrics.remoteCandidateType]
    .filter((value): value is NonNullable<typeof value> => value !== null && value !== 'unknown')
    .join(' → ');
  return types === '' ? path : `${path} · ${types}`;
}

function formatCodec(metrics: ConnectionMetrics | null): string {
  const mimeType = metrics?.codec?.mimeType;
  return mimeType ? mimeType.replace(/^video\//u, '').toUpperCase() : '—';
}

function formatResolution(metrics: ConnectionMetrics | null): string {
  return metrics?.frameWidth && metrics.frameHeight
    ? `${metrics.frameWidth} × ${metrics.frameHeight}`
    : '—';
}

function formatNumber(value: number | null | undefined, digits: number): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : value.toFixed(digits).replace(/\.0$/u, '');
}

function formatMilliseconds(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : `${Math.round(value)} ms`;
}

function formatPercentage(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : `${value.toFixed(1)}%`;
}

function formatIceState(value: RTCIceConnectionState | null): string {
  return value === null ? 'NEW' : value.replace(/-/gu, ' ').toUpperCase();
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m7 9 5 5 5-5" />
    </svg>
  );
}
